import React, { useState, useEffect, useCallback } from 'react';
import { admissionAPI, patientAPI, doctorAPI } from '../../lib/api';
import { useWebSocket } from '../../context/WebSocketContext';
import { formatDate, formatDateTime, getStatusColor, apiErrorMessage } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { BedDouble, Plus, Search, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import DrawingPad from '../../components/DrawingPad';
import { logger } from '../../lib/logger';

const AdmissionList = () => {
  const [admissions, setAdmissions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    room_number: '',
    bed_number: '',
    admission_reason: '',
    attending_doctor_id: '',
    admission_notes: '',
  });
  const [drawingData, setDrawingData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [admRes, patRes, docRes] = await Promise.all([
        admissionAPI.getAll(params),
        patientAPI.getAll({}),
        doctorAPI.getAll(),
      ]);
      
      // Handle paginated response structures
      setAdmissions(admRes.data.admissions || admRes.data || []);
      setPatients(patRes.data.patients || patRes.data || []);
      setDoctors(docRes.data.doctors || docRes.data || []);
    } catch (error) {
      logger.error('Error loading admission data:', error);
      toast.error(apiErrorMessage(error, 'Failed to load admission data. Click to retry.'));
      setAdmissions([]);
      setPatients([]);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);


  useEffect(() => {
    fetchData();
  }, [statusFilter, fetchData]);

  const { lastMessage } = useWebSocket();
  useEffect(() => {
    if (lastMessage?.type === 'patient_event' && ['admitted', 'discharged'].includes(lastMessage.event)) {
      fetchData();
    }
  }, [lastMessage, fetchData]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        handwritten_notes: drawingData,
      };
      await admissionAPI.create(data);
      toast.success('Patient admitted successfully');
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        room_number: '',
        bed_number: '',
        admission_reason: '',
        attending_doctor_id: '',
        admission_notes: '',
      });
      setDrawingData(null);
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create admission'));
    }
  };

  const handleDischarge = async (admissionId) => {
    if (!window.confirm('Are you sure you want to discharge this patient?')) return;
    try {
      await admissionAPI.discharge(admissionId);
      toast.success('Patient discharged successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to discharge patient');
    }
  };

  return (
    <div data-testid="admissions-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Admissions</h1>
            <p className="text-slate-500">Manage indoor patient admissions</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="new-admission-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Admission
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">New Patient Admission</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>Patient *</Label>
                  <Select value={formData.patient_id} onValueChange={(v) => setFormData({...formData, patient_id: v})}>
                    <SelectTrigger data-testid="select-patient">
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Room Number *</Label>
                    <Input
                      value={formData.room_number}
                      onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                      placeholder="e.g., 101"
                      required
                      data-testid="input-room"
                    />
                  </div>
                  <div>
                    <Label>Bed Number *</Label>
                    <Input
                      value={formData.bed_number}
                      onChange={(e) => setFormData({...formData, bed_number: e.target.value})}
                      placeholder="e.g., A"
                      required
                      data-testid="input-bed"
                    />
                  </div>
                </div>
                <div>
                  <Label>Attending Doctor *</Label>
                  <Select value={formData.attending_doctor_id} onValueChange={(v) => setFormData({...formData, attending_doctor_id: v})}>
                    <SelectTrigger data-testid="select-doctor">
                      <SelectValue placeholder="Select doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctors.map(d => (
                        <SelectItem key={d.id} value={d.id}>Dr. {d.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Admission Reason *</Label>
                  <Input
                    value={formData.admission_reason}
                    onChange={(e) => setFormData({...formData, admission_reason: e.target.value})}
                    placeholder="Reason for admission"
                    required
                    data-testid="input-reason"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.admission_notes}
                    onChange={(e) => setFormData({...formData, admission_notes: e.target.value})}
                    placeholder="Additional notes"
                    data-testid="input-notes"
                  />
                </div>
                
                {/* Drawing Pad for Handwritten Admission Notes */}
                <DrawingPad
                  label="Handwritten Notes (Optional)"
                  onSave={(data) => {
                    setDrawingData(data);
                    toast.success('Handwritten notes saved');
                  }}
                />
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-admission">Admit Patient</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <BedDouble className="w-5 h-5 text-red-500" />
                Admission Records
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="admitted">Admitted</SelectItem>
                  <SelectItem value="discharged">Discharged</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading admissions...</div>
            ) : admissions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No admissions found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Admission ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Room/Bed</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Admitted On</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admissions.map((adm) => (
                    <TableRow key={adm.id} data-testid={`admission-row-${adm.id}`}>
                      <TableCell className="font-mono text-sm">{adm.admission_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{adm.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{adm.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>Room {adm.room_number}, Bed {adm.bed_number}</TableCell>
                      <TableCell className="max-w-xs truncate">{adm.admission_reason}</TableCell>
                      <TableCell>{formatDateTime(adm.admission_date)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(adm.status)}>{adm.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {adm.status === 'admitted' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDischarge(adm.id)}
                            data-testid={`discharge-${adm.id}`}
                          >
                            <LogOut className="w-4 h-4 mr-1" />
                            Discharge
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdmissionList;
